import { Component, Input, OnChanges, OnInit } from "@angular/core";
import { NzFormatEmitEvent, NzTreeNodeOptions } from "ng-zorro-antd/tree";
import { take } from "rxjs/operators";
import { IGoal } from "@gtmhub/goals/models";
import { localize } from "@gtmhub/localization";
import { IMetric } from "@gtmhub/okrs/metrics";
import { hasCurrentUserRole } from "@gtmhub/users";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ILinksSpecDetail, Link, LinkSourceTarget, LinkSpecDetail, Links, LinksDTO, RelatedLink } from "@webapp/links/models/links.models";
import { IRelatedItem, IRelatedItemsOnChanges, ISuggestedItem, IUpdateLinkArgs } from "@webapp/links/models/related-items.models";
import { LinksFacade } from "@webapp/links/services/links-facade.service";
import { generateLink, generateRelation, generateSpec } from "@webapp/links/utils/related-items.utils";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";

@Component({
  selector: "related-items",
  templateUrl: "./related-items.component.html",
  styleUrls: ["./related-items.component.less"],
})
export class RelatedItemsComponent implements OnInit, OnChanges {
  @Input() public links: Links<LinkSpecDetail>;
  @Input() public linkSourceTargetId: string;
  @Input() public linkSourceTarget: LinkSourceTarget;

  public relatedItems: IRelatedItem[] = [];
  public hasViewOnlyRole: boolean;
  public tooltipTitle: string;
  public treeNodes: NzTreeNodeOptions[] = [];

  private defaultLink: Link = "related";

  constructor(private linksFacade: LinksFacade, private goalsFacade: GoalsFacade, private broadcastService: BroadcastService) {}

  public ngOnInit(): void {
    this.relatedItems = this.getLinksRelatedItems(this.links);
    this.hasViewOnlyRole = hasCurrentUserRole("view-only");
    this.tooltipTitle = localize("insufficient_permissions_to_manage_related_items");
  }

  public ngOnChanges(changes: IRelatedItemsOnChanges): void {
    if (!changes.links.firstChange) {
      this.relatedItems = this.getLinksRelatedItems(changes.links.currentValue);
    }
  }

  public addRelatedItem(): void {
    const subscribe = this.goalsFacade.getAll$({ fields: ["name,ownerIds,metrics{id,name,ownerIds}"], limit: 5 }).subscribe(({ items }) => {
      // TODO: Make this method with switchMap() pipe + async pipe in view

      this.treeNodes = items.map((goal) => {
        const children: NzTreeNodeOptions[] = goal.metrics
          ? goal.metrics.map((metric) => ({ key: metric.id, title: metric.name, isLeaf: true, itemType: "metric" }))
          : [];

        return { key: goal.id, title: goal.name, children: children, isLeaf: !children.length, itemType: "goal" };
      });

      subscribe.unsubscribe();
    });
  }

  public onNodeClick({ node }: NzFormatEmitEvent): void {
    this.createLink({ item: <Metric>{ id: node.origin.key }, itemType: node.origin.itemType });
  }

  public createLink(suggestedItem: ISuggestedItem): void {
    const newLink: LinksDTO<Link> = {
      linkType: this.defaultLink,
      sourceId: this.linkSourceTargetId,
      sourceType: this.linkSourceTarget,
      targetId: suggestedItem.item.id,
      targetType: suggestedItem.itemType === "goal" ? "goals" : "metrics",
    };
    this.linksFacade
      .createLink$(newLink)
      .pipe(take(1))
      .subscribe(() => {
        this.treeNodes = [];
        const args = { reason: "goalChanged", goalId: this.linkSourceTargetId, newLink };
        this.broadcastService.emit("okrs.goalsChanged", { ...args });
      });
  }

  public deleteLink(relatedItem: IRelatedItem): void {
    const link = generateLink(this.linkSourceTargetId, this.linkSourceTarget, relatedItem);

    const subscribe = this.linksFacade.deleteLink$(link).subscribe(() => {
      const args = { reason: "goalChanged", goalId: this.linkSourceTargetId, link };
      this.broadcastService.emit("okrs.goalsChanged", { ...args });
      subscribe.unsubscribe();
    });
  }

  public updateLink(args: IUpdateLinkArgs): void {
    const { sourceId, sourceType } = generateLink(this.linkSourceTargetId, this.linkSourceTarget, args.relatedItem);

    const relation = generateRelation(args.relatedItem.id, args.relatedItem.iconType);
    const linksToCreate: Links<LinkSpecDetail> = generateSpec(args.relatedItem.linkType, relation);
    const linksToDelete: Links<LinkSpecDetail> = generateSpec(args.linkTypeToDelete, relation);

    const subscribe = this.linksFacade.patchBulkLink$({ sourceId, sourceType }, { linksToCreate, linksToDelete }).subscribe(() => {
      subscribe.unsubscribe();
    });
  }

  private getLinksRelatedItems(links: Links<LinkSpecDetail>): IRelatedItem[] {
    const relatedItems: IRelatedItem[] = [];

    if (links) {
      Object.keys(links.spec).forEach((linkType) => {
        links.spec[linkType].forEach((linkSpec) => {
          const relatedItem: IRelatedItem = {
            linkType: linkType as RelatedLink,
            iconType: linkSpec.type,
            id: linkSpec.id,
            name: this.getRelatedItemName(links, linkSpec),
          };
          relatedItems.push(relatedItem);
        });
      });
    }

    return relatedItems;
  }

  private getRelatedItemName(links: Links<LinkSpecDetail>, linkSpec: ILinksSpecDetail<LinkSpecDetail>): string {
    const type: LinkSourceTarget = linkSpec.type === "goal" ? "goals" : "metrics";

    return (links.expanded[type] as Array<IGoal | IMetric>).find((item) => {
      return item.id === linkSpec.id;
    })?.name;
  }
}
